import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react'
import 'echarts-wordcloud'
import { Upload, Button, Progress, message, Select, Spin, Tabs } from 'antd'
import {
    InboxOutlined, CloudUploadOutlined, FileTextOutlined,
    CloseCircleOutlined, SmileOutlined, PieChartOutlined, DownloadOutlined,
    ApartmentOutlined,
} from '@ant-design/icons'
import * as echarts from 'echarts'
import * as XLSX from 'xlsx'
import { analyzeSentimentFile, analyzeSentimentText, subscribeStream, analyzeFile, analyzeText, analyzeNetwork, fetchKeywordsFile, fetchKeywordsText, analyzeCommentTag, analyzeCommentTagText } from '../../api/wordcloud'
import styles from './index.module.css'

const { Dragger } = Upload
const { TextArea } = require('antd').Input

const COLORS = [
    '#1677ff', '#13c2c2', '#52c41a', '#fa8c16', '#eb2f96',
    '#722ed1', '#2f54eb', '#08979c', '#389e0d', '#d46b08',
    '#c41d7f', '#531dab', '#096dd9', '#006d75', '#237804',
    '#ad4e00', '#9e1068', '#391085',
]

const SentimentPage = () => {
    const [inputMode, setInputMode] = useState('file')
    const [fileList, setFileList] = useState([])
    const [textContent, setTextContent] = useState('')
    const [analyzing, setAnalyzing] = useState(false)
    const [taskStatuses, setTaskStatuses] = useState({})
    const [result, setResult] = useState(null) // { items, summary }
    const [segmentResults, setSegmentResults] = useState([])
    const [networkData, setNetworkData] = useState(null)
    const [commentTagData, setCommentTagData] = useState([]) // top_props [{adj, sentiment, count}]
    const [commentTagType, setCommentTagType] = useState(4)
    const [sentimentCloudLoading, setSentimentCloudLoading] = useState(false)

    const pieRef = useRef(null)
    const pieInstance = useRef(null)
    const scatterRef = useRef(null)
    const scatterInstance = useRef(null)
    const kwCloudRef = useRef(null)
    const kwCloudInstance = useRef(null)
    const sentimentCloudRef = useRef(null)
    const sentimentCloudInstance = useRef(null)
    const graphRef = useRef(null)
    const graphInstance = useRef(null)

    const [messageApi, contextHolder] = message.useMessage()

    /* ── Computed stats ── */
    const totalChars = useMemo(() => {
        if (!result?.items) return 0
        return result.items.reduce((sum, item) => {
            const s = item.sentence || ''
            // strip BOM character if present
            return sum + s.replace(/^\ufeff/, '').length
        }, 0)
    }, [result])

    /* ── Build score distribution for scatter ── */
    const buildDistribution = useCallback((items) => {
        const binMap = new Map()
        items.forEach(item => {
            let score = item.base_score ?? 0
            // round to nearest 0.5
            const binned = Math.round(score * 2) / 2
            // clamp to [-10, 10]
            const clamped = Math.max(-10, Math.min(10, binned))
            binMap.set(clamped, (binMap.get(clamped) || 0) + 1)
        })
        return Array.from(binMap.entries())
            .sort((a, b) => a[0] - b[0])
            .map(([x, y]) => [x, y])
    }, [])

    /* ── Render pie chart ── */
    const renderPie = useCallback((summary) => {
        if (!pieRef.current) return
        if (pieInstance.current) {
            pieInstance.current.dispose()
            pieInstance.current = null
        }
        pieInstance.current = echarts.init(pieRef.current)
        const total = summary.total_count || 1
        const pieData = [
            {
                value: summary.negative_count,
                name: '负面',
                itemStyle: { color: '#f5222d' },
                ratio: summary.negative_count / total,
            },
            {
                value: summary.neutral_count,
                name: '中性',
                itemStyle: { color: '#fa8c16' },
                ratio: summary.neutral_count / total,
            },
            {
                value: summary.positive_count,
                name: '正面',
                itemStyle: { color: '#52c41a' },
                ratio: summary.positive_count / total,
            },
        ]
        pieInstance.current.setOption({
            tooltip: {
                trigger: 'item',
                formatter: (p) => `${p.name}：${p.value} 条 (${(p.data.ratio * 100).toFixed(2)}%)`,
            },
            series: [{
                type: 'pie',
                radius: '79%',
                center: ['50%', '54%'],
                data: pieData,
                label: {
                    show: true,
                    position: 'inside',
                    formatter: (p) => `${p.name}\n${(p.data.ratio * 100).toFixed(2)}%`,
                    fontSize: 12,
                    fontWeight: 'bold',
                    color: '#fff',
                },
                labelLine: { show: false },
                emphasis: {
                    itemStyle: { shadowBlur: 10, shadowColor: 'rgba(0,0,0,0.2)' },
                },
            }],
        })
    }, [])

    /* ── Render scatter distribution chart ── */
    const renderScatter = useCallback((items) => {
        if (!scatterRef.current) return
        if (scatterInstance.current) {
            scatterInstance.current.dispose()
            scatterInstance.current = null
        }
        scatterInstance.current = echarts.init(scatterRef.current)
        const distData = buildDistribution(items)

        scatterInstance.current.setOption({
            title: {
                text: '情绪值与数量的分布情况',
                left: 'center',
                top: 8,
                textStyle: { fontSize: 13, fontWeight: 700, color: '#1a1a2e' },
            },
            tooltip: {
                trigger: 'item',
                formatter: (p) => `情绪值：${p.value[0]} 分<br/>评价条数：${p.value[1]} 条`,
            },
            grid: { top: 62, bottom: 48, left: 62, right: 4, containLabel: true },
            xAxis: {
                type: 'value',
                min: -10,
                max: 10,
                interval: 5,
                axisLabel: {
                    formatter: (val) => {
                        if (val === -10) return '非常不满意'
                        if (val === 10) return '非常满意'
                        return `${val}分`
                    },
                    fontSize: 11,
                    color: '#888',
                    margin: 14,
                },
                splitLine: { lineStyle: { color: '#f0f2f8' } },
                axisLine: { lineStyle: { color: '#ddd' } },
            },
            yAxis: {
                type: 'value',
                name: '条数',
                nameLocation: 'middle',
                nameGap: 42,
                nameTextStyle: { fontSize: 11, color: '#888' },
                axisLabel: { fontSize: 11, color: '#888' },
                splitLine: { lineStyle: { color: '#f0f2f8' } },
                axisLine: { show: false },
            },
            series: [
                {
                    type: 'scatter',
                    data: distData,
                    symbolSize: 9,
                    itemStyle: { color: '#5b8ff9', opacity: 0.85 },
                },
                {
                    // vertical reference line at x=0
                    type: 'line',
                    markLine: {
                        silent: true,
                        symbol: 'none',
                        lineStyle: { type: 'dashed', color: '#bbb', width: 1.5 },
                        data: [{ xAxis: 0 }],
                        label: { show: false },
                    },
                    data: [],
                },
            ],
        })
    }, [buildDistribution])

    /* ── Render word cloud ── */
    const renderKwWordCloud = useCallback((segRows) => {
        if (!kwCloudRef.current) return
        if (kwCloudInstance.current) {
            kwCloudInstance.current.dispose()
            kwCloudInstance.current = null
        }
        const el = kwCloudRef.current
        kwCloudInstance.current = echarts.init(el)
        const data = segRows
            .filter(row => row[0] && Number(row[1]) > 0)
            .map(row => ({ name: String(row[0]), value: Number(row[1]) }))
            .sort((a, b) => b.value - a.value)
            .slice(0, 400)
        kwCloudInstance.current.setOption({
            tooltip: { show: true, formatter: (p) => `${p.name}：${p.value} 次` },
            series: [{
                type: 'wordCloud',
                shape: 'circle',
                left: 'center',
                top: 'center',
                width: '100%',
                height: '100%',
                sizeRange: [12, 52],
                rotationRange: [0, 0],
                rotationStep: 0,
                gridSize: 6,
                drawOutOfBound: false,
                data,
                textStyle: {
                    fontFamily: 'sans-serif',
                    fontWeight: 'bold',
                    color: () => COLORS[Math.floor(Math.random() * COLORS.length)],
                },
                emphasis: {
                    focus: 'self',
                    textStyle: { shadowBlur: 10, shadowColor: 'rgba(0,0,0,0.25)' },
                },
            }],
        })
    }, [])

    /* ── Render sentiment word cloud ── */
    const renderSentimentWordCloud = useCallback((topProps) => {
        if (!sentimentCloudRef.current) return
        if (sentimentCloudInstance.current) {
            sentimentCloudInstance.current.dispose()
            sentimentCloudInstance.current = null
        }
        const el = sentimentCloudRef.current
        sentimentCloudInstance.current = echarts.init(el)
        const data = topProps.map(item => ({
            name: String(item.adj),
            value: Number(item.count),
            textStyle: {
                color: item.sentiment === 2 ? '#f5222d'
                    : item.sentiment === 0 ? '#1677ff'
                    : item.sentiment === 1 ? '#52c41a'
                    : '#888888',
            },
        }))
        sentimentCloudInstance.current.setOption({
            tooltip: { show: true, formatter: (p) => `${p.name}：${p.value} 次` },
            series: [{
                type: 'wordCloud',
                shape: 'circle',
                left: 'center',
                top: 'center',
                width: '100%',
                height: '100%',
                sizeRange: [12, 52],
                rotationRange: [0, 0],
                rotationStep: 0,
                gridSize: 6,
                drawOutOfBound: false,
                data,
                textStyle: {
                    fontFamily: 'sans-serif',
                    fontWeight: 'bold',
                    color: (wordItem) => wordItem.textStyle?.color || '#888',
                },
                emphasis: {
                    focus: 'self',
                    textStyle: { shadowBlur: 10, shadowColor: 'rgba(0,0,0,0.25)' },
                },
            }],
        })
    }, [])

    /* ── Render force-directed graph ── */
    const renderGraph = useCallback((data) => {
        if (!graphRef.current) return
        if (graphInstance.current) {
            graphInstance.current.dispose()
            graphInstance.current = null
        }
        graphInstance.current = echarts.init(graphRef.current)

        const rawNodes = data.nodes || data.vertices || []
        const rawEdges = data.edges || data.links || []

        const filteredEdges = rawEdges.filter(e => {
            const w = e.weight != null ? e.weight : (e.value != null ? e.value : 0)
            return w >= 2
        })

        const activeWords = new Set()
        filteredEdges.forEach(e => { activeWords.add(e.source); activeWords.add(e.target) })
        const filteredNodes = rawNodes.filter(n => {
            const name = n.name || n.id || n.word || ''
            return activeWords.has(name)
        })

        const weightSum = new Map()
        filteredEdges.forEach(e => {
            const w = e.value != null ? e.value : (e.weight != null ? e.weight : 0)
            weightSum.set(e.source, (weightSum.get(e.source) || 0) + w)
            weightSum.set(e.target, (weightSum.get(e.target) || 0) + w)
        })

        const scoredNodes = filteredNodes.map((n, i) => {
            const name = n.name || n.id || n.word || String(i)
            const score = Number(n.score != null ? n.score : (n.value != null ? n.value : (n.weight != null ? n.weight : 0)))
            const wDeg = weightSum.get(name) || 0
            return { raw: n, name, score, wDeg }
        }).sort((a, b) => b.wDeg - a.wDeg)

        const total = scoredNodes.length
        const innerCount = Math.max(1, Math.round(total * 0.16))
        const middleCount = Math.max(1, Math.round(total * 0.34))
        const outerCount = total - innerCount - middleCount

        const RING_RADII = [120, 260, 420]
        const RING_NODE_SIZE = [22, 14, 9]
        const RING_FONT_SIZE = [15, 12, 10]
        const RING_COLOR_BORDER = ['#b91c1c', '#f59e0b', '#94a3b8']
        const tierIndexOf = (i) => {
            if (i < innerCount) return 0
            if (i < innerCount + middleCount) return 1
            return 2
        }

        const tierMap = new Map()
        scoredNodes.forEach((s, i) => tierMap.set(s.name, tierIndexOf(i)))

        const placeOnRing = (count, radius, phaseOffset = 0) => {
            const pts = []
            for (let j = 0; j < count; j++) {
                const theta = (j / count) * Math.PI * 2 + phaseOffset
                pts.push({ x: Math.cos(theta) * radius, y: Math.sin(theta) * radius })
            }
            return pts
        }
        const innerPts = placeOnRing(innerCount, RING_RADII[0], -Math.PI / 2)
        const middlePts = placeOnRing(middleCount, RING_RADII[1], -Math.PI / 2 + Math.PI / middleCount)
        const outerPts = placeOnRing(outerCount, RING_RADII[2], -Math.PI / 2)

        const nodes = scoredNodes.map((s, i) => {
            const tier = tierIndexOf(i)
            const idxInTier = tier === 0 ? i : tier === 1 ? i - innerCount : i - innerCount - middleCount
            const pos = tier === 0 ? innerPts[idxInTier] : tier === 1 ? middlePts[idxInTier] : outerPts[idxInTier]
            return {
                name: s.name,
                value: s.score,
                x: pos.x,
                y: pos.y,
                symbolSize: RING_NODE_SIZE[tier],
                itemStyle: {
                    color: COLORS[i % COLORS.length],
                    borderColor: RING_COLOR_BORDER[tier],
                    borderWidth: tier === 0 ? 2 : 1,
                    shadowBlur: tier === 0 ? 12 : 6,
                    shadowColor: tier === 0 ? 'rgba(185,28,28,0.35)' : 'rgba(0,0,0,0.10)',
                },
                label: {
                    show: true,
                    fontSize: RING_FONT_SIZE[tier],
                    color: tier === 0 ? '#7f1d1d' : '#222',
                    fontWeight: tier === 0 ? 'bold' : 'normal',
                    borderRadius: 4,
                    backgroundColor: 'rgba(255,255,255,0.8)',
                    padding: [2, 6],
                },
            }
        })

        const edgeVals = filteredEdges.map(e => e.value != null ? e.value : (e.weight != null ? e.weight : 0.5))
        const maxEdge = Math.max(...edgeVals)
        const minEdge = Math.min(...edgeVals)

        function edgeColor(val, tier) {
            const t = (val - minEdge) / (maxEdge - minEdge + 1e-8)
            if (tier === 0) return t > 0.5 ? '#7f1d1d' : '#b91c1c'
            if (tier === 1) return t > 0.5 ? '#7f1d1d' : '#b91c1c'
            return t > 0.5 ? '#991b1b' : '#475569'
        }

        const links = filteredEdges.map((e) => {
            const w = e.value != null ? e.value : (e.weight != null ? e.weight : 0.5)
            const norm = maxEdge > minEdge ? (w - minEdge) / (maxEdge - minEdge) : 1
            const ts = tierMap.get(e.source) ?? 2
            const tt = tierMap.get(e.target) ?? 2
            const edgeTier = Math.max(ts, tt)
            const baseWidth = edgeTier === 0 ? 3 : edgeTier === 1 ? 1 : 0.4
            const widthScale = edgeTier === 0 ? 5 : edgeTier === 1 ? 1.8 : 0.8
            const opacity = edgeTier === 0 ? 0.85 + norm * 0.15 : edgeTier === 1 ? 0.7 + norm * 0.25 : 0.55 + norm * 0.3
            return {
                source: e.source,
                target: e.target,
                value: w,
                lineStyle: {
                    width: baseWidth + norm * widthScale,
                    opacity,
                    color: edgeColor(w, edgeTier),
                    curveness: 0.12,
                },
                symbol: ['none', 'none'],
            }
        })

        graphInstance.current.setOption({
            backgroundColor: '#fafbff',
            tooltip: {
                trigger: 'item',
                borderRadius: 8,
                backgroundColor: 'rgba(255,255,255,0.97)',
                textStyle: { color: '#222', fontSize: 14 },
                formatter: (params) => {
                    if (params.dataType === 'node')
                        return `<b style="color:${params.color}">${params.name}</b><br/>权重: <b>${Number(params.value).toExponential(2)}</b>`
                    return `<b style="color:#b91c1c">${params.data.source}</b> → <b style="color:#b91c1c">${params.data.target}</b><br/>强度: <b>${Number(params.data.value).toExponential(2)}</b>`
                },
            },
            series: [{
                type: 'graph',
                layout: 'none',
                data: nodes,
                links,
                roam: true,
                draggable: false,
                left: 'center',
                top: 'center',
                label: { show: true, position: 'right', fontSize: 12, color: '#333', formatter: '{b}' },
                lineStyle: { curveness: 0.12, opacity: 0.6 },
                emphasis: {
                    focus: 'adjacency',
                    lineStyle: { width: 4, color: '#b91c1c', opacity: 0.95 },
                    itemStyle: { borderColor: '#b91c1c', borderWidth: 3 },
                    label: { fontWeight: 'bold', color: '#b91c1c' },
                },
            }],
        })
        graphInstance.current.resize()
    }, [])

    useEffect(() => {
        const onResize = () => {
            pieInstance.current?.resize()
            scatterInstance.current?.resize()
            kwCloudInstance.current?.resize()
            sentimentCloudInstance.current?.resize()
            graphInstance.current?.resize()
        }
        window.addEventListener('resize', onResize)
        return () => window.removeEventListener('resize', onResize)
    }, [])

    useEffect(() => {
        if (result) {
            setTimeout(() => renderPie(result.summary), 60)
            setTimeout(() => renderScatter(result.items), 60)
        }
    }, [result, renderPie, renderScatter])

    useEffect(() => {
        if (segmentResults.length === 0 && commentTagData.length === 0) return
        // Delay long enough for flex layout to settle so both containers have their final widths
        const timer = setTimeout(() => {
            if (segmentResults.length > 0) renderKwWordCloud(segmentResults)
            if (commentTagData.length > 0) renderSentimentWordCloud(commentTagData)
        }, 120)
        return () => clearTimeout(timer)
    }, [segmentResults, commentTagData, renderKwWordCloud, renderSentimentWordCloud])

    useEffect(() => {
        if (networkData) {
            setTimeout(() => renderGraph(networkData), 60)
        }
    }, [networkData, renderGraph])

    /* ── Main analysis flow ── */
    const handleAnalyze = async () => {
        if (inputMode === 'file' && fileList.length === 0) return messageApi.warning('请先选择要分析的文件')
        if (inputMode === 'text' && !textContent.trim()) return messageApi.warning('请输入要分析的文本内容')

        setAnalyzing(true)
        setResult(null)
        setSegmentResults([])
        setNetworkData(null)
        setCommentTagData([])
        setTaskStatuses({
            sentiment:  { status: 'loading', message: '正在提交情感分析任务...', percent: 0 },
            keywords:   { status: 'loading', message: '正在获取关键词...',        percent: null },
            commentTag: { status: 'loading', message: '正在提交情感词云任务...', percent: 0 },
        })

        const currentFile = inputMode === 'file' ? fileList[0] : null
        const upd = (key, patch) => setTaskStatuses(p => ({ ...p, [key]: { ...p[key], ...patch } }))

        try {
            // Phase 1 (sentiment) + Phase 2 (keywords) + Phase 5 (commentTag) run in parallel
            const [, kw] = await Promise.all([

                // Phase 1: Sentiment analysis
                (async () => {
                    try {
                        const { task_id } = inputMode === 'file'
                            ? await analyzeSentimentFile(currentFile)
                            : await analyzeSentimentText(textContent)
                        upd('sentiment', { message: '情感分析中...' })
                        await subscribeStream(
                            task_id,
                            (prog) => upd('sentiment', { percent: prog.total > 0 ? Math.round(prog.current / prog.total * 100) : 0, message: prog.message }),
                            (data) => {
                                setResult(data)
                                upd('sentiment', { status: 'done', message: '情感分析完成', percent: 100 })
                                messageApi.success('评论分析完成')
                            },
                            (err) => {
                                upd('sentiment', { status: 'error', message: err.message || '情感分析失败' })
                                if (!err?.handled) messageApi.error(err.message || '评论分析失败')
                            },
                        )
                    } catch (err) {
                        upd('sentiment', { status: 'error', message: err.message || '情感分析失败' })
                        if (!err?.handled) messageApi.error(err.message || '评论分析失败')
                    }
                })(),

                // Phase 2: Keywords — result is passed to Phase 3 & 4
                (async () => {
                    try {
                        const rawKw = inputMode === 'file'
                            ? await fetchKeywordsFile(currentFile, 300)
                            : await fetchKeywordsText(textContent, 300)
                        const result = (Array.isArray(rawKw) ? rawKw : [])
                            .map(item => ({ word: String(item?.word ?? ''), score: Number(item?.score) || 0 }))
                            .filter(item => item.word)
                        upd('keywords', { status: 'done', message: `关键词获取完成（${result.length} 个）` })
                        return result
                    } catch (err) {
                        upd('keywords', { status: 'error', message: err.message || '关键词获取失败' })
                        if (!err?.handled) messageApi.error(err.message || '关键词获取失败')
                        return []
                    }
                })(),

                // Phase 5: Comment Tag sentiment word cloud
                (async () => {
                    try {
                        const { task_id: ctTaskId } = inputMode === 'file'
                            ? await analyzeCommentTag(currentFile, commentTagType)
                            : await analyzeCommentTagText(textContent, commentTagType)
                        upd('commentTag', { message: '情感词云分析中...' })
                        await subscribeStream(
                            ctTaskId,
                            (prog) => upd('commentTag', { percent: prog.total > 0 ? Math.round(prog.current / prog.total * 100) : 0, message: prog.message }),
                            (ctResult) => {
                                const topProps = ctResult?.summary?.top_props
                                if (Array.isArray(topProps) && topProps.length > 0) {
                                    setCommentTagData(topProps)
                                    messageApi.success('情感词云图生成完成')
                                }
                                upd('commentTag', { status: 'done', message: '情感词云图生成完成', percent: 100 })
                            },
                            (err) => {
                                upd('commentTag', { status: 'error', message: err.message || '情感词云分析失败' })
                                if (!err?.handled) messageApi.error(err.message || '情感词云分析失败')
                            },
                        )
                    } catch (err) {
                        upd('commentTag', { status: 'error', message: err.message || '情感词云分析失败' })
                        if (!err?.handled) messageApi.error(err.message || '情感词云分析失败')
                    }
                })(),
            ])

            // Phase 3 (segmentation) and Phase 4 (network) both depend on kw from Phase 2
            const vocabulary = (kw || []).map(k => k.word)
            setTaskStatuses(p => ({
                ...p,
                segment: { status: 'loading', message: '正在提交分词分析任务...', percent: 0 },
                network: { status: 'loading', message: '正在提交网络图分析任务...', percent: 0 },
            }))

            await Promise.all([

                // Phase 3: Segmentation (for word cloud)
                (async () => {
                    try {
                        const { task_id: segTaskId } = inputMode === 'file'
                            ? await analyzeFile(currentFile, vocabulary)
                            : await analyzeText(textContent, vocabulary)
                        upd('segment', { message: '分词分析中...' })
                        await subscribeStream(
                            segTaskId,
                            (prog) => upd('segment', { percent: prog.total > 0 ? Math.round(prog.current / prog.total * 100) : 0, message: prog.message }),
                            (segResult) => {
                                setSegmentResults(Array.isArray(segResult) ? segResult : [])
                                upd('segment', { status: 'done', message: '分词分析完成', percent: 100 })
                            },
                            (err) => {
                                upd('segment', { status: 'error', message: err.message || '分词分析失败' })
                                if (!err?.handled) messageApi.error(err.message || '分词分析失败')
                            },
                        )
                    } catch (err) {
                        upd('segment', { status: 'error', message: err.message || '分词分析失败' })
                        if (!err?.handled) messageApi.error(err.message || '分词分析失败')
                    }
                })(),

                // Phase 4: Network graph
                (async () => {
                    try {
                        const { task_id: netTaskId } = inputMode === 'file'
                            ? await analyzeNetwork(currentFile, null, kw || [], 50)
                            : await analyzeNetwork(null, textContent, kw || [], 50)
                        upd('network', { message: '网络图分析中...' })
                        await subscribeStream(
                            netTaskId,
                            (prog) => upd('network', { percent: prog.total > 0 ? Math.round(prog.current / prog.total * 100) : 0, message: prog.message }),
                            (netResult) => {
                                setNetworkData(netResult)
                                upd('network', { status: 'done', message: '关系网络图生成完成', percent: 100 })
                                messageApi.success('关系网络图生成完成')
                            },
                            (err) => {
                                upd('network', { status: 'error', message: err.message || '网络图分析失败' })
                                if (!err?.handled) messageApi.error(err.message || '网络图分析失败')
                            },
                        )
                    } catch (err) {
                        upd('network', { status: 'error', message: err.message || '网络图分析失败' })
                        if (!err?.handled) messageApi.error(err.message || '网络图分析失败')
                    }
                })(),
            ])

            setAnalyzing(false)
        } catch (err) {
            setAnalyzing(false)
            if (!err?.handled) messageApi.error(err.message || '请求失败，请稍后重试')
        }
    }

    /* ── Download source text ── */
    const downloadSourceText = () => {
        if (!result?.items?.length) return messageApi.warning('暂无数据')
        const text = result.items
            .map(item => (item.sentence || '').replace(/^\ufeff/, ''))
            .join('\n')
        const blob = new Blob([text], { type: 'text/plain;charset=utf-8' })
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = '源文本.txt'
        a.click()
        URL.revokeObjectURL(url)
    }

    const downloadAnalysisExcel = () => {
        if (!result?.items?.length) return messageApi.warning('暂无数据')

        const sheetData = [
            ['原文', '分数', '情感'],
            ...result.items.map((item) => [
                String((item?.sentence || '').replace(/^\ufeff/, '')),
                Number(item?.sentiment ?? 0),
                Number(item?.base_score ?? 0),
            ]),
        ]

        const workbook = XLSX.utils.book_new()
        const worksheet = XLSX.utils.aoa_to_sheet(sheetData)
        XLSX.utils.book_append_sheet(workbook, worksheet, '分析结果')
        XLSX.writeFile(workbook, '分析结果.xlsx')
    }

    const downloadKwCloudImage = () => {
        if (!kwCloudInstance.current) return messageApi.warning('词云图尚未生成')
        const url = kwCloudInstance.current.getDataURL({ type: 'png', pixelRatio: 2, backgroundColor: '#fff' })
        const a = document.createElement('a')
        a.href = url
        a.download = '特征词云图.png'
        a.click()
    }

    const downloadSentimentCloudImage = () => {
        if (!sentimentCloudInstance.current) return messageApi.warning('情感词云图尚未生成')
        const url = sentimentCloudInstance.current.getDataURL({ type: 'png', pixelRatio: 2, backgroundColor: '#fff' })
        const a = document.createElement('a')
        a.href = url
        a.download = '情感词云图.png'
        a.click()
    }

    const handleCommentTagTypeChange = async (val) => {
        setCommentTagType(val)
        if (inputMode === 'file' && fileList.length === 0) return
        if (inputMode === 'text' && !textContent.trim()) return
        setSentimentCloudLoading(true)
        setCommentTagData([])
        try {
            const { task_id: ctTaskId } = inputMode === 'file'
                ? await analyzeCommentTag(fileList[0], val)
                : await analyzeCommentTagText(textContent, val)
            await subscribeStream(
                ctTaskId,
                () => {},
                (ctResult) => {
                    setSentimentCloudLoading(false)
                    const topProps = ctResult?.summary?.top_props
                    if (Array.isArray(topProps) && topProps.length > 0) {
                        setCommentTagData(topProps)
                    } else {
                        messageApi.warning('未获取到情感词云数据')
                    }
                },
                (err) => {
                    setSentimentCloudLoading(false)
                    if (!err?.handled) messageApi.error(err.message || '情感词云分析失败')
                },
            )
        } catch (err) {
            setSentimentCloudLoading(false)
            if (!err?.handled) messageApi.error(err.message || '情感词云分析失败')
        }
    }

    const downloadGraphImage = () => {
        if (!graphInstance.current) return messageApi.warning('关系网络图尚未生成')
        const url = graphInstance.current.getDataURL({ type: 'png', pixelRatio: 2, backgroundColor: '#fafbff' })
        const a = document.createElement('a')
        a.href = url
        a.download = '关系网络图.png'
        a.click()
    }

    const uploadProps = {
        name: 'file',
        multiple: false,
        accept: '.docx,.txt',
        maxCount: 1,
        fileList,
        showUploadList: false,
        beforeUpload: (file) => {
            const ext = file.name.split('.').pop().toLowerCase()
            const allowed = ['docx', 'txt']
            if (!allowed.includes(ext)) {
                messageApi.error(`仅支持 ${allowed.map(e => e.toUpperCase()).join(' / ')} 格式`)
                return Upload.LIST_IGNORE
            }
            if (file.size > 10 * 1024 * 1024) {
                messageApi.error('文件大小不能超过 10MB')
                return Upload.LIST_IGNORE
            }
            setFileList([file])
            return false
        },
        onRemove: () => setFileList([]),
    }

    const _taskOrder = ['sentiment', 'keywords', 'commentTag', 'segment', 'network']
    const _activeTasks = _taskOrder.map(k => taskStatuses[k]).filter(Boolean)
    const progressPercent = _activeTasks.length === 0 ? 0 : Math.round(
        _activeTasks.reduce((sum, st) => {
            if (st.status === 'done' || st.status === 'error') return sum + 100
            return sum + (st.percent || 0)
        }, 0) / _activeTasks.length
    )
    const progressMessage = (_activeTasks.find(st => st.status === 'loading') ?? _activeTasks[_activeTasks.length - 1])?.message ?? ''

    return (
        <div className={styles.pageWrapper}>
            {contextHolder}
            <div className={styles.layout}>

                {/* ── 左侧面板 ── */}
                <div className={styles.leftPanel}>
                    <div className={styles.sectionCard}>
                        <div className={styles.sectionHeader}>
                            <FileTextOutlined className={styles.sectionIcon} />
                            <span>输入内容</span>
                        </div>
                        <Tabs activeKey={inputMode} onChange={setInputMode} size="small" items={[
                            {
                                key: 'file',
                                label: <span><CloudUploadOutlined /> 上传文件</span>,
                                children: (
                                    <div className={styles.uploadArea}>
                                        <Dragger {...uploadProps} disabled={analyzing} className={styles.uploadDragger}>
                                            {fileList.length > 0 ? (
                                                <div className={styles.fileDisplay}>
                                                    <CloudUploadOutlined style={{ fontSize: 22, color: '#1677ff', flexShrink: 0 }} />
                                                    <span className={styles.fileName}>{fileList[0].name}</span>
                                                    <CloseCircleOutlined
                                                        className={styles.fileRemove}
                                                        onClick={(e) => { e.stopPropagation(); setFileList([]) }}
                                                        title="移除文件"
                                                    />
                                                </div>
                                            ) : (
                                                <div className={styles.uploadPrompt}>
                                                    <InboxOutlined style={{ color: '#1677ff', fontSize: 28 }} />
                                                    <p className={styles.uploadText}>点击或拖拽文件上传</p>
                                                    <p className={styles.uploadHint}>DOCX / TXT，不超过 10MB</p>
                                                </div>
                                            )}
                                        </Dragger>
                                    </div>
                                ),
                            },
                            {
                                key: 'text',
                                label: <span><FileTextOutlined /> 输入文本</span>,
                                children: (
                                    <TextArea rows={4} placeholder="粘贴或输入文本（支持中英文）..." value={textContent} onChange={(e) => setTextContent(e.target.value)} disabled={analyzing} showCount maxLength={200000} />
                                ),
                            },
                        ]} />
                    </div>

                    <div className={styles.sectionCard}>
                        <div className={styles.sectionHeader}>
                            <ApartmentOutlined className={styles.sectionIcon} />
                            <span>评论相关行业</span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <span style={{ fontSize: 13, color: '#666', flexShrink: 0 }}>行业：</span>
                            <Select
                                size="small"
                                value={commentTagType}
                                onChange={handleCommentTagTypeChange}
                                style={{ width: '100%' }}
                                disabled={analyzing}
                                options={[
                                    { value: 1, label: '酒店' },
                                    { value: 2, label: 'KTV' },
                                    { value: 3, label: '丽人' },
                                    { value: 4, label: '美食餐饮' },
                                    { value: 5, label: '旅游' },
                                    { value: 6, label: '健康' },
                                    { value: 7, label: '教育' },
                                    { value: 8, label: '商业' },
                                    { value: 9, label: '房产' },
                                    { value: 10, label: '汽车' },
                                    { value: 11, label: '生活' },
                                    { value: 12, label: '购物' },
                                    { value: 13, label: '3C' },
                                ]}
                            />
                        </div>
                    </div>

                    <Button
                        type="primary"
                        size="large"
                        className={styles.analyzeBtn}
                        onClick={handleAnalyze}
                        loading={analyzing}
                        icon={<SmileOutlined />}
                        disabled={analyzing}
                    >
                        {analyzing ? '分析中...' : '开始分析'}
                    </Button>
                </div>

                {/* ── 右侧面板 ── */}
                <div className={styles.rightPanel}>
                    <div className={styles.rightInner}>

                        {/* 分析结果卡片 */}
                        {result && (
                            <div className={`${styles.sectionCard} ${styles.statsCard}`} style={{ padding: '0 16px 14px' }}>
                                <div className={styles.statsRow}>

                                    {/* 左侧统计表格 */}
                                    <div className={styles.statsTableSide} style={{ paddingTop: 14 }}>
                                        <div className={styles.statsTableWrap}>
                                            <div className={`${styles.sectionHeader} ${styles.statsTableHeader}`}>
                                                <PieChartOutlined className={styles.sectionIcon} />
                                                <span>数据统计</span>
                                            </div>
                                            <table className={styles.statsTable}>
                                                <tbody>
                                                    <tr>
                                                        <td className={styles.statsLabel}>总字数</td>
                                                        <td className={styles.statsValue}>{totalChars.toLocaleString()}个字</td>
                                                    </tr>
                                                    <tr>
                                                        <td className={styles.statsLabel}>总条数</td>
                                                        <td className={styles.statsValue}>{result.summary.total_count}条</td>
                                                    </tr>
                                                    <tr>
                                                        <td className={styles.statsLabel}>正面条数</td>
                                                        <td className={styles.statsValue}>{result.summary.positive_count}条</td>
                                                    </tr>
                                                    <tr>
                                                        <td className={styles.statsLabel}>中性条数</td>
                                                        <td className={styles.statsValue}>{result.summary.neutral_count}条</td>
                                                    </tr>
                                                    <tr>
                                                        <td className={styles.statsLabel}>负面条数</td>
                                                        <td className={styles.statsValue}>{result.summary.negative_count}条</td>
                                                    </tr>
                                                    <tr>
                                                        <td className={styles.statsLabel}>下载</td>
                                                        <td className={styles.statsValue}>
                                                            <span
                                                                className={styles.statsDownloadLink}
                                                                onClick={downloadSourceText}
                                                            >
                                                                源文本.txt
                                                            </span>
                                                            <span
                                                                className={styles.statsDownloadLink}
                                                                onClick={downloadAnalysisExcel}
                                                            >
                                                                分析结果.xlsx
                                                            </span>
                                                        </td>
                                                    </tr>
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>

                                    {/* 中部扇形图 */}
                                    <div className={styles.statsPieSide} ref={pieRef} style={{ paddingTop: 14 }} />

                                    {/* 右侧分布散点图 */}
                                    <div className={styles.statsScatterSide} ref={scatterRef} style={{ paddingTop: 14 }} />
                                </div>
                            </div>
                        )}

                        {/* 空状态 */}
                        {!result && !analyzing && (
                            <div className={styles.emptyState}>
                                <SmileOutlined className={styles.emptyIcon} />
                                <p className={styles.emptyText}>
                                    上传文本文件，点击「开始分析」<br />
                                    分析情感倾向并生成分布图表
                                </p>
                            </div>
                        )}

                        {/* 分析进度 — 有数据时显示紧凑条幅，无数据时显示居中圆圈 */}
                        {analyzing && (result || segmentResults.length > 0 || commentTagData.length > 0) && (
                            <div className={styles.inlineProgress}>
                                <Spin size="small" />
                                <div style={{ flex: 1, minWidth: 0 }}>
                                    <Progress
                                        percent={progressPercent}
                                        size="small"
                                        strokeColor={{ '0%': '#1a73e8', '100%': '#7c3aed' }}
                                        style={{ marginBottom: 3 }}
                                    />
                                    <span className={styles.inlineProgressMsg}>{progressMessage}</span>
                                </div>
                            </div>
                        )}

                        {analyzing && !result && segmentResults.length === 0 && commentTagData.length === 0 && (
                            <div className={styles.progressCenter}>
                                <div className={styles.progressBox}>
                                    <span className={styles.progressTitle}>评论分析中</span>
                                    <Progress
                                        type="circle"
                                        percent={progressPercent}
                                        width={80}
                                        strokeColor={{ '0%': '#1a73e8', '100%': '#7c3aed' }}
                                        trailColor="#e8eaf0"
                                        format={(p) => (
                                            <span style={{ fontSize: 14, fontWeight: 700, color: '#1a73e8' }}>
                                                {p}%
                                            </span>
                                        )}
                                    />
                                    <p className={styles.progressMessage}>{progressMessage}</p>
                                </div>
                            </div>
                        )}

                        {/* 词云图 */}
                        {(segmentResults.length > 0 || commentTagData.length > 0 || sentimentCloudLoading) && (
                            <div className={`${styles.sectionCard} ${styles.kwCard}`} style={{ padding: 0, overflow: 'hidden' }}>
                                <div style={{ display: 'flex', gap: 0, height: 400 }}>

                                    {/* 特征词云图 */}
                                    <div style={{ flex: 1, minWidth: 0, overflow: 'hidden', borderRight: '1px solid #f0f0f0', display: 'flex', flexDirection: 'column' }}>
                                        {/* 标题行 */}
                                        <div style={{ height: 32, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#fafafa', borderBottom: '1px solid #f0f0f0', position: 'relative' }}>
                                            <span style={{ fontSize: 12, fontWeight: 600, color: '#555' }}>特征词云图</span>
                                            <Button
                                                type="primary"
                                                size="small"
                                                icon={<DownloadOutlined />}
                                                onClick={downloadKwCloudImage}
                                                style={{ position: 'absolute', left: 8, fontSize: 11, padding: '0 8px', height: 24, opacity: 0.85 }}
                                                title="下载特征词云图"
                                            />
                                        </div>
                                        {/* 画布 */}
                                        <div ref={kwCloudRef} style={{ flex: 1, minHeight: 0 }} />
                                    </div>

                                    {/* 情感词云图 */}
                                    <div style={{ flex: 1, minWidth: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                                        {/* 标题行 */}
                                        <div style={{ height: 32, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#fafafa', borderBottom: '1px solid #f0f0f0', position: 'relative' }}>
                                            <Button
                                                type="primary"
                                                size="small"
                                                icon={<DownloadOutlined />}
                                                onClick={downloadSentimentCloudImage}
                                                style={{ position: 'absolute', left: 8, fontSize: 11, padding: '0 8px', height: 24, opacity: 0.85 }}
                                                title="下载情感词云图"
                                            />
                                            <span style={{ fontSize: 12, fontWeight: 600, color: '#555' }}>情感词云图</span>
                                        </div>
                                        {/* 画布区（加载时显示遮罩） */}
                                        <div style={{ flex: 1, minHeight: 0, position: 'relative' }}>
                                            {sentimentCloudLoading && (
                                                <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(255,255,255,0.85)', zIndex: 3 }}>
                                                    <Spin tip="加载中..." />
                                                </div>
                                            )}
                                            <div ref={sentimentCloudRef} style={{ width: '100%', height: '100%' }} />
                                        </div>
                                    </div>

                                </div>
                            </div>
                        )}

                        {/* 关系网络图 */}
                        {networkData && (
                            <div className={`${styles.sectionCard} ${styles.graphCard}`}>
                                <div className={styles.sectionHeader}>
                                    <ApartmentOutlined className={styles.sectionIcon} />
                                    <span>关系网络图</span>
                                </div>
                                <div style={{ position: 'relative', width: '70%', aspectRatio: '1 / 1' }}>
                                    <div ref={graphRef} style={{ width: '100%', height: '100%' }} />
                                    <span style={{
                                        position: 'absolute', top: 8, right: 8,
                                        fontSize: 11, color: '#bbb', fontWeight: 400,
                                        pointerEvents: 'none',
                                    }}>
                                        可拖拽节点 · 滚轮缩放
                                    </span>
                                    <Button
                                        type="primary"
                                        size="small"
                                        icon={<DownloadOutlined />}
                                        onClick={downloadGraphImage}
                                        style={{
                                            position: 'absolute', top: 8, left: 8,
                                            fontSize: 11, padding: '0 8px', height: 24,
                                            opacity: 0.85,
                                        }}
                                        title="下载关系网络图"
                                    />
                                </div>
                            </div>
                        )}

                    </div>
                </div>

            </div>
        </div>
    )
}

export default SentimentPage
